import { useEffect, useState } from "react";
import { Outlet, useNavigate, Navigate } from "react-router-dom";
import { Alert, Box, Loader, Center, Text, Group, Badge } from "@mantine/core";
import { getSession, verifyToken, setSession, clearSession } from "@/lib/auth";
import type { AccessSession } from "@/content/types";
import { PageContainer } from "./PageContainer";

type AuthState =
  | { status: "checking" }
  | { status: "ok"; session: AccessSession }
  | { status: "no-token" }
  | { status: "invalid"; reason: string };

export function PrivateLayout() {
  const [state, setState] = useState<AuthState>({ status: "checking" });
  const navigate = useNavigate();

  useEffect(() => {
    const existing = getSession();
    if (!existing) {
      setState({ status: "no-token" });
      return;
    }
    // Re-verify on mount so a stale session doesn't render private UI.
    verifyToken(existing.token)
      .then((fresh) => {
        setSession(fresh);
        setState({ status: "ok", session: fresh });
      })
      .catch((err: unknown) => {
        clearSession();
        setState({
          status: "invalid",
          reason: err instanceof Error ? err.message : "unknown",
        });
      });
  }, []);

  useEffect(() => {
    if (state.status !== "invalid") return;
    const id = window.setTimeout(() => {
      navigate("/access?error=" + encodeURIComponent(state.reason), { replace: true });
    }, 800);
    return () => window.clearTimeout(id);
  }, [state, navigate]);

  if (state.status === "no-token") {
    return <Navigate to="/access" replace />;
  }

  if (state.status === "invalid") {
    return (
      <PageContainer>
        <Center mih="40vh">
          <Alert color="red" title="Session expired or invalid">
            Redirecting to access page…
          </Alert>
        </Center>
      </PageContainer>
    );
  }

  if (state.status === "checking") {
    return (
      <PageContainer>
        <Center mih="40vh">
          <Loader />
        </Center>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box>
        <Group justify="space-between" mb="lg">
          <Text size="sm" c="dimmed">
            Signed in as <Text component="span" c="var(--color-starlight)">{state.session.label}</Text>
          </Text>
          <Group gap="xs">
            {state.session.scope.map((s) => (
              <Badge key={s} size="xs" variant="light" color="indigo">
                {s}
              </Badge>
            ))}
          </Group>
        </Group>
        <Outlet context={{ session: state.session }} />
      </Box>
    </PageContainer>
  );
}

// Helper so private routes can read the session via useOutletContext()
export type PrivateContext = { session: AccessSession };
